import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { inquiryApi, isApiError } from "@bw/shared/api";
import { useAuth, useCoachingInquiryOffer, useCoachingInquiryOffers } from "@bw/shared/hooks";
import { MessageThread } from "../components/MessagesLayout";
import { CoachingProposalTimelineCard } from "../components/CoachingProposalTimelineCard";
import { revokeConversation } from "../../../common/hooks/conversationCacheAuthority";
import { useIsNarrow } from "../hooks/useIsNarrow";

export function InquiryThreadPage() {
  const { conversationId = "" } = useParams();
  const { user } = useAuth();
  return (
    <InquiryThread
      key={`${user?.uid}:${conversationId}`}
      conversationId={conversationId}
    />
  );
}

function InquiryThread({ conversationId }: { conversationId: string }) {
  const { user } = useAuth();
  const location = useLocation(), navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [draft, setDraft] = useState(() => {
    const value = location.state?.coachingInquiryDraft;
    return value?.actorId === user?.uid && value?.conversationId === conversationId && typeof value?.content === "string" ? value.content : "";
  });
  useEffect(() => {
    if (location.state?.coachingInquiryDraft) navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);
  const client = useQueryClient();
  const isNarrow = useIsNarrow();
  const query = useQuery({
    queryKey: ["social", "inquiry", user?.uid, conversationId],
    queryFn: ({ signal }) => inquiryApi.get(conversationId, signal),
    enabled: Boolean(user && conversationId),
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: 15_000,
    retry: false,
  });
  const mismatch =
    query.data !== undefined && query.data.conversation_id !== conversationId;
  const offers = useCoachingInquiryOffers(conversationId,
    Boolean(query.data?.target?.kind === "coach" && !query.isError && !mismatch));
  const focusedRevision = searchParams.get("proposal");
  const pagedOffers = offers.data?.pages.flatMap((page) => page.items) ?? [];
  const exactOffer = useCoachingInquiryOffer(conversationId, focusedRevision ?? "",
    Boolean(focusedRevision && query.data?.target?.kind === "coach" && !query.isError && !mismatch &&
      !pagedOffers.some((item) => item.revision_id === focusedRevision)));
  useEffect(() => {
    if (query.data?.target?.kind !== "coach" || query.isError || mismatch) return;
    const timer = window.setInterval(() => void offers.refetch(), 15_000);
    return () => window.clearInterval(timer);
  }, [conversationId, query.data?.target?.kind, query.isError, mismatch, offers.refetch]);
  const exactCurrent = exactOffer.isCurrent ? exactOffer.data : undefined;
  const visibleOffers = exactCurrent && !pagedOffers.some((item) => item.revision_id === exactCurrent.revision_id)
    ? [...pagedOffers, exactCurrent] : pagedOffers;
  const canActOffer = () => Boolean(user && query.isFetchedAfterMount && !query.isError &&
    !mismatch && offers.isFetchedAfterMount && !offers.isError && !offers.isFetching);
  const timelineItems = useMemo(() => visibleOffers.flatMap((proposal) => {
    const items = [{
      id: `proposal-${proposal.revision_id}`,
      occurredAt: proposal.issued_at,
      content: <CoachingProposalTimelineCard proposal={proposal} canAct={canActOffer} />,
    }];
    if (proposal.resolved_action && proposal.resolved_at) {
      const labels = { accept: "Proposal accepted", request_revision: "Changes requested",
        decline: "Proposal declined", withdraw: "Proposal withdrawn" } as const;
      items.push({ id: `proposal-resolution-${proposal.revision_id}`, occurredAt: proposal.resolved_at,
        content: <Box role="status" sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: "action.hover",
          color: "text.secondary", fontSize: "0.875rem", fontWeight: 700 }}>{labels[proposal.resolved_action]}</Box> });
    }
    return items;
  }), [visibleOffers, canActOffer]);
  const focusTimelineItemId = focusedRevision && visibleOffers.some((item) => item.revision_id === focusedRevision)
    ? `proposal-${focusedRevision}` : undefined;
  useEffect(() => {
    if (
      mismatch ||
      (isApiError(query.error) && [403, 404].includes(query.error.status))
    )
      void revokeConversation(client, conversationId);
  }, [client, conversationId, query.error, mismatch]);
  if (query.isError || mismatch)
    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Alert severity="warning">
          This inquiry is unavailable. Your access may have changed.
        </Alert>
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => void query.refetch()}>
            Retry
          </Button>
          <Button size="small" component={RouterLink} to="/community/inquiries">
            Back to inquiries
          </Button>
        </Stack>
      </Stack>
    );
  if (!query.isFetchedAfterMount || !query.data || !user)
    return (
      <Typography role="status" sx={{ p: 2 }}>
        Loading inquiry…
      </Typography>
    );
  const inquiry = query.data;
  return (
    <Stack
      sx={{
        width: "100%",
        minWidth: 0,
        minHeight: 0,
        height: {
          xs: "calc(100dvh - 8.75rem - env(safe-area-inset-bottom, 0px))",
          sm: "100%",
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ px: 2, py: 1, alignItems: "center", flexWrap: "wrap" }}
      >
        <Typography variant="body2">
          {inquiry.target_name ?? inquiry.facility_name ?? "Inquiry"}
        </Typography>
        <Chip size="small" label={inquiry.lifecycle} />
        {inquiry.initial_messages_remaining !== null ? (
          <Typography variant="caption" color="text.secondary">
            Initial messages remaining: {inquiry.initial_messages_remaining}
          </Typography>
        ) : null}
      </Stack>
      {offers.isError ? <Alert severity="warning" sx={{ mx: 2 }}
        action={<Button size="small" onClick={() => void offers.refetch()}>Retry</Button>}>
        Proposal history is unavailable.
      </Alert> : null}
      {exactOffer.isError ? <Alert severity="warning" sx={{ mx: 2 }}>
        This proposal is unavailable. Its access or status may have changed.
      </Alert> : null}
      {offers.hasNextPage ? <Button size="small" sx={{ alignSelf: "flex-start", mx: 2 }}
        disabled={offers.isFetchingNextPage} onClick={() => void offers.fetchNextPage()}>
        Earlier proposals
      </Button> : null}
      <MessageThread
        initialDraft={draft}
        conversationId={conversationId}
        title={inquiry.topic}
        headingLevel="h1"
        backTo="/community/inquiries"
        backLabel="Back to inquiries"
        authenticated={Boolean(user)}
        isNarrow={isNarrow}
        writeAccess={inquiry.write_access}
        readOnlyReason={
          inquiry.requester_user_id === user.uid &&
          inquiry.admission === "pending" &&
          inquiry.lifecycle === "open" &&
          inquiry.initial_messages_remaining === 0
            ? "awaiting_staff_reply"
            : undefined
        }
        canModerateMessages={inquiry.can_moderate_messages === true}
        canDeleteOwnMessages={inquiry.can_delete_own_messages === true}
        timelineItems={timelineItems}
        focusTimelineItemId={focusTimelineItemId}
        onMessageConfirmed={() => {
          setDraft("");
          void query.refetch();
        }}
      />
    </Stack>
  );
}
